import React, { useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Zap, Shield, BarChart3, CreditCard, Tag, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Paywall({ subscription, onSuccess }) {
  const [coupon, setCoupon] = useState("");
  const [applying, setApplying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [couponResult, setCouponResult] = useState(null);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/subscription/apply-coupon`, { code: coupon }, { withCredentials: true });
      toast.success(res.data.message);
      setCouponResult(res.data);
      if (res.data.coupon?.type === "lifetime" || res.data.coupon?.type === "free_days") {
        setTimeout(() => onSuccess?.(), 1000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid coupon");
    } finally { setApplying(false); }
  };

  const handlePayment = async () => {
    setPaying(true);
    const loaded = await loadRazorpayScript();
    if (!loaded) { toast.error("Payment service failed to load"); setPaying(false); return; }

    try {
      const res = await axios.post(`${API}/subscription/create-order`, {}, { withCredentials: true });
      const { order_id, amount, currency, key_id, user_name, user_email } = res.data;

      const options = {
        key: key_id || RAZORPAY_KEY,
        amount,
        currency,
        name: "MarketJournal",
        description: "Yearly Premium Subscription",
        order_id,
        prefill: { name: user_name, email: user_email },
        theme: { color: "#34D399" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount,
            }, { withCredentials: true });
            toast.success("Payment successful! Welcome to MarketJournal Premium.");
            onSuccess?.();
          } catch (err) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error("Payment failed: " + response.error.description);
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPaying(false);
    }
  };

  const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--t-bg)" }} data-testid="paywall">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-emerald-400/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--t-text)" }}>Your Free Trial Has Ended</h1>
          {trialEnd && <p className="text-sm mt-2" style={{ color: "var(--t-text-muted)" }}>Trial expired on {trialEnd}</p>}
          <p className="text-sm mt-1" style={{ color: "var(--t-text-muted)" }}>
            Upgrade to continue using all features of MarketJournal
          </p>
        </div>

        <div className="glass rounded-2xl p-6 mb-4" data-testid="pricing-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold" style={{ color: "var(--t-text)" }}>Premium Plan</h2>
              <p className="text-xs" style={{ color: "var(--t-text-muted)" }}>Everything you need to trade better</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-400">
                {couponResult?.coupon?.type === "percent_off"
                  ? `Rs.${Math.round(4999 * (1 - couponResult.coupon.value / 100)).toLocaleString()}`
                  : "Rs.4,999"}
              </p>
              <p className="text-xs" style={{ color: "var(--t-text-muted)" }}>per year</p>
              {couponResult?.coupon?.type === "percent_off" && (
                <p className="text-xs text-red-400 line-through">Rs.4,999</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-5">
            {[
              { icon: BarChart3, text: "Unlimited Trades" },
              { icon: Shield, text: "AI Reports & Analysis" },
              { icon: Zap, text: "Paper Trading" },
              { icon: CreditCard, text: "Risk Calculator" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03]">
                <Icon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                <span className="text-xs font-medium" style={{ color: "var(--t-text-secondary)" }}>{text}</span>
              </div>
            ))}
          </div>

          <Button data-testid="pay-btn" onClick={handlePayment} disabled={paying}
            className="w-full h-12 rounded-xl text-sm font-bold bg-emerald-400 text-black hover:bg-emerald-300">
            {paying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><CreditCard className="w-4 h-4 mr-2" /> Pay Rs.{couponResult?.coupon?.type === "percent_off" ? Math.round(4999 * (1 - couponResult.coupon.value / 100)).toLocaleString() : "4,999"} / year</>}
          </Button>
        </div>

        {/* Coupon */}
        <div className="glass rounded-2xl p-4" data-testid="coupon-section">
          <p className="text-xs font-medium mb-2" style={{ color: "var(--t-text-muted)" }}>Have a coupon code?</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
              <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                placeholder="Enter code" className="h-10 pl-9 rounded-xl text-xs font-mono uppercase bg-white/5 border-white/10" style={{ color: "var(--t-text)" }}
                onKeyDown={(e) => e.key === "Enter" && applyCoupon()} />
            </div>
            <Button data-testid="apply-coupon-btn" onClick={applyCoupon} disabled={applying}
              variant="outline" className="h-10 rounded-xl text-xs border-white/10 hover:bg-white/5 px-4" style={{ color: "var(--t-text-muted)" }}>
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
            </Button>
          </div>
          {couponResult && (
            <div className="flex items-center gap-2 mt-2 text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">{couponResult.message}</span>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] mt-4" style={{ color: "var(--t-text-muted)" }}>
          Secure payment via Razorpay. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
